import { Box, Divider, LinearProgress, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';

import {
  Icon,
  Row,
  Text,
  MobileButton,
  MobileInput,
  Background,
  MobileSelect,
} from '@/components/ui';
import { Header, Layout, useFooterContext } from '@/components/layout';
import { exportIcon, excelIcon } from '@/assets';
import newProjectIcon from '@/assets/new-project-icon.svg';
import { useSitesStore } from '@/features/sites/store';
import { useProjectsStore } from '@/features/projects/store';

import { SiteCard } from '../components/SiteCard';
import type { Site } from '../types';
import {
  useWordExport,
  type ExportSiteData,
  type ExportProject,
} from '../hooks/useWordExport';
import { useExcelExport } from '../hooks';
import { showGlobalSuccess, useToast } from '@/components/feedback';
import { ConfirmModal } from '@/components';

interface SitesPageProps {
  searchParams?: { projectId?: number };
}

export const SitesPage = ({ searchParams = {} }: SitesPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sites, materials } = useSitesStore();
  const { getSites, getMaterialsList, getSiteMaterials, getCompressedPhotos } =
    useSitesStore(state => state.actions);
  const { currentProject: project, materialListFormats } = useProjectsStore();
  const { updateProject, getMaterialListFormats, getProject, deleteProject } =
    useProjectsStore(state => state.actions);
  const { exportToWord } = useWordExport();
  const { exportToExcel } = useExcelExport();
  const { setFooterPlusButton } = useFooterContext();
  const { showSuccess } = useToast();
  const [searchText, setSearchText] = useState('');
  const [projectName, setProjectName] = useState('');
  const [discount, setDiscount] = useState<number>(0);
  const [currentProjectMLFormat, setCurrentProjectMLFormat] =
    useState<string>('');
  const [localProjectMLFormat, setLocalProjectMLFormat] = useState<string>('');
  const [showProjectNameError, setShowProjectNameError] = useState(false);
  const [discountError, setDiscountError] = useState(false);
  const [isReportGenerating, setIsReportGenerating] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportProgressMessage, setExportProgressMessage] = useState('');
  const [showModalConfirmDeleteProject, setShowModalConfirmDeleteProject] =
    useState<boolean>(false);
  const [showModalConfirmDeleteAllSites, setShowModalConfirmDeleteAllSites] =
    useState<boolean>(false);
  // Get projectId from search params
  const projectId = searchParams.projectId || null;

  // Filter sites by search text
  const filteredSites = useMemo(() => {
    if (!searchText) return sites;
    const searchLower = searchText.toLowerCase().trim();
    return sites.filter(site => {
      const values = Object.values(site);
      return values.some(v =>
        v?.toString().toLowerCase().includes(searchLower)
      );
    });
  }, [sites, searchText]);

  useEffect(() => {
    getMaterialsList();
    getMaterialListFormats();
    if (projectId) {
      getSites(projectId);
      getProject(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    setFooterPlusButton(true, handleAddSite);

    return () => {
      setFooterPlusButton(false);
    };
  }, []);

  // Update local state when project data changes
  useEffect(() => {
    if (project) {
      setProjectName(project.projectName);
      setDiscount(project.discount);
      if (project.materialFormat) {
        setLocalProjectMLFormat(project.materialFormat.id.toString());
        setCurrentProjectMLFormat(project.materialFormat.id.toString());
      } else {
        setLocalProjectMLFormat('');
        setCurrentProjectMLFormat('');
      }
    }
  }, [project]);

  const mLFormats = useMemo(() => {
    return materialListFormats.map(item => ({
      value: item.id.toString(),
      label: item.formatName,
    }));
  }, [materialListFormats]);

  const updateProjectName = async () => {
    if (!projectName?.trim()) {
      setShowProjectNameError(true);
      return;
    }

    if (project) {
      const result = await updateProject({
        id: project.id,
        discount: discount,
        projectName: projectName,
      });
      if (result.success && result.message) {
        showGlobalSuccess(result.message);
      }
    }
  };

  const updateProjectMLFormat = async () => {
    if (project && currentProjectMLFormat) {
      const result = await updateProject({
        id: project.id,
        discount: discount,
        projectName: projectName,
        materialFormatId: parseFloat(currentProjectMLFormat) || null,
      });
      if (result.success) {
        setLocalProjectMLFormat(currentProjectMLFormat);
        if (result.message) {
          showGlobalSuccess(result.message);
        }
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  const updateDiscount = async () => {
    if (isNaN(discount) || discount < 0 || discount > 100) {
      setDiscountError(true);
      return;
    }

    if (project) {
      await updateProject({
        id: project.id,
        discount: discount,
        projectName: projectName,
      });
    }
  };

  // Navigation handlers
  const handleSelectedSite = (site: Site) => {
    navigate({
      to: `/projects/${Number(site.projectId)}/sites/${Number(site.id)}`,
    });
  };

  const handleAddSite = () => {
    if (projectId) {
      navigate({
        to: `/projects/${projectId}/sites/add`,
      });
    } else {
      navigate({ to: '/projects' });
    }
  };

  // Export handlers
  const handleExportWord = async () => {
    setIsReportGenerating(true);
    setExportProgress(0);
    setExportProgressMessage('');

    try {
      // Get current project info
      if (!projectId) {
        throw new Error('No project selected');
      }

      if (!project) {
        throw new Error('Project not found');
      }

      setExportProgressMessage(t('export.loading_materials'));
      setExportProgress(10);

      // Load materials and site materials if not already loaded
      if (materials.length === 0) {
        await getMaterialsList();
      }

      setExportProgressMessage(t('export.loading_sites_data'));
      setExportProgress(20);

      // Get site materials for all sites

      setExportProgressMessage(t('export.preparing_data'));
      setExportProgress(30);

      // Prepare data for export
      const exportData: ExportSiteData[] = [];
      const totalSites = filteredSites.length;

      for (let i = 0; i < filteredSites.length; i++) {
        const site = filteredSites[i];
        const siteMaterialsData = await getSiteMaterials(site.id);
        setExportProgressMessage(
          t('export.processing_site', { current: i + 1, total: totalSites })
        );
        setExportProgress(30 + ((i + 1) / totalSites) * 50); // 30% to 80%
        // Get site materials for this specific site

        // Map materials with costs
        const materialsForExport = siteMaterialsData
          .map(siteMaterial => {
            const material = materials.find(
              m => m.id === siteMaterial.materialId
            );
            if (!material) return null;

            const materialCost = parseFloat(
              (material.unitCost * siteMaterial.quantity).toFixed(2)
            );
            return {
              quantity: siteMaterial.quantity,
              materialCode: material.materialCode,
              unitType: material.unitType,
              longDescription: material.longDescription,
              unitCost: material.unitCost,
              materialCost: materialCost,
              siteName: site.siteName,
            };
          })
          .filter(Boolean) as Array<{
          quantity: number;
          materialCode: string;
          unitType: string;
          longDescription: string;
          unitCost: number;
          materialCost: number;
          siteName: string;
        }>;

        // Get photos for this specific site
        const photosResult = await getCompressedPhotos(site.id);
        const sitePhotos = photosResult || [];
        const imagesWithData = sitePhotos.map(photo => ({
          ...photo,
          photoData:
            (photo as { compressedPhotoData?: string; photoData?: string })
              .compressedPhotoData ||
            (photo as { compressedPhotoData?: string; photoData?: string })
              .photoData ||
            photo.base64Image ||
            '',
          width: 800, // Default width
          height: 600, // Default height
          orientation: 1, // Default orientation
        }));

        exportData.push({
          data: site,
          materials: materialsForExport,
          images: imagesWithData,
        });
      }

      const projectForExport: ExportProject = {
        id: project.id,
        projectName: project.projectName,
        discount: project.discount || 0,
      };

      setExportProgressMessage(t('export.generating_document'));
      setExportProgress(85);

      await exportToWord(exportData, projectForExport, () => {
        setExportProgressMessage(t('export.completing'));
        setExportProgress(100);

        // Small delay to show 100% completion
        setTimeout(() => {
          setIsReportGenerating(false);
          setExportProgress(0);
          setExportProgressMessage('');
        }, 500);
      });
    } catch (error) {
      console.error('Export failed:', error);
      setIsReportGenerating(false);
      setExportProgress(0);
      setExportProgressMessage('');
    }
  };

  const handleExportExcel = async () => {
    setIsReportGenerating(true);
    try {
      // Get current project info
      if (!projectId) {
        throw new Error('No project selected');
      }

      if (!project) {
        throw new Error('Project not found');
      }

      // Use filtered sites for export
      await exportToExcel(
        filteredSites,
        project.projectName,
        project.discount || 0,
        () => {
          setIsReportGenerating(false);
        }
      );
    } catch (error) {
      console.error('Excel export failed:', error);
      setIsReportGenerating(false);
    }
  };

  const onChangeDiscount = (value: string) => {
    setDiscountError(false);
    const discount = parseInt(value);
    if (discount > 100) {
      setDiscount(100);
      return;
    }
    setDiscount(discount);
  };

  const onChangeMaterialsListFormat = (value: string) => {
    setCurrentProjectMLFormat(value);
  };

  const onChangeProjectName = (value: string) => {
    setShowProjectNameError(false);
    setProjectName(value);
  };
  const handleDeleteProject = () => {
    setShowModalConfirmDeleteProject(true);
  };
  const checkIfProjectHasSites = async () => {
    if (sites.length > 0) {
      setShowModalConfirmDeleteProject(false);
      setShowModalConfirmDeleteAllSites(true);
      return;
    }
    tryDeleteProject();
  };
  const tryDeleteProject = async () => {
    if (!projectId) return;
    const success = await deleteProject(projectId);
    if (success) {
      showSuccess(t('project_deleted_success'));
      navigate({
        to: `/projects`,
        search: {},
      });
    }
  };
  return (
    <Layout>
      <Box sx={{ position: 'relative' }}>
        {isReportGenerating && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(255, 255, 255, 0.95)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            <Box sx={{ width: '80%', maxWidth: 400 }}>
              <Typography
                variant='h6'
                sx={{
                  textAlign: 'center',
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 'bold',
                }}
              >
                {t('export.document_title')}
              </Typography>

              <Box sx={{ mb: 1 }}>
                <LinearProgress
                  variant='determinate'
                  value={exportProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem', // 14px
                  }}
                >
                  {exportProgressMessage}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'primary.main',
                    fontWeight: 'bold',
                    fontSize: '0.875rem', // 14px
                  }}
                >
                  {Math.round(exportProgress)}%
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <Header
          title={project ? project.projectName : t('irrigation_system_survey')}
          subTitle={project ? t('project_name_title') : undefined}
          hideBackButton={projectId ? false : true}
          linkBack={projectId ? '/projects' : undefined}
          handleDelete={handleDeleteProject}
        />

        <Box>
          {/* Project management section */}
          {project && (
            <Box>
              <Row alignItems={'flex-end'} sx={{ mb: '1rem' }}>
                {' '}
                {/* 16px */}
                <MobileInput
                  label={`${t('project_name_label_project_management')}`}
                  name='project_name'
                  error={showProjectNameError}
                  helperText={
                    showProjectNameError ? 'this_field_cannot_be_empty' : ''
                  }
                  onChange={value => onChangeProjectName(value.target.value)}
                  value={projectName}
                  compact
                  placeholder={''}
                  fullWidth
                />
                <Box width='1.25rem'></Box> {/* 20px */}
                <MobileButton
                  disabled={
                    project.projectName == projectName || !projectName.trim()
                  }
                  btnColor='#1C5B9C'
                  sx={{ width: '10rem', height: '2.5rem', mt: 0 }} // 160px, 40px
                  onClick={updateProjectName}
                >
                  <Text>{`${t('save_project_name_project_management')}`}</Text>
                </MobileButton>
              </Row>
              <Row alignItems={'flex-end'} sx={{ mb: '1rem' }}>
                {' '}
                {/* 16px */}
                <MobileInput
                  label={`${t('discount_label_project_management')}`}
                  name='discount'
                  error={discountError}
                  helperText={discountError ? 'this_field_cannot_be_empty' : ''}
                  onChange={value => onChangeDiscount(value.target.value)}
                  value={discount}
                  compact
                  placeholder={''}
                  fullWidth
                  type='number'
                />
                <Box width='1.25rem'></Box> {/* 20px */}
                <MobileButton
                  disabled={project.discount == discount}
                  btnColor='#1C5B9C'
                  sx={{ width: '10rem', height: '2.5rem', mt: 0 }} // 160px, 40px
                  onClick={updateDiscount}
                >
                  <Text>{`${t('save_discount_project_management')}`}</Text>
                </MobileButton>
              </Row>
              <Row alignItems={'flex-end'} sx={{ mb: '1rem' }}>
                {' '}
                {/* 16px */}
                <MobileSelect
                  title={t('materials_list_format_label_project_management')}
                  items={mLFormats}
                  error={false}
                  noSelectLabel={''}
                  onChange={value => onChangeMaterialsListFormat(value)}
                  value={currentProjectMLFormat}
                  fullWidth
                />
                <Box width='1.25rem'></Box> {/* 20px */}
                <MobileButton
                  disabled={localProjectMLFormat == currentProjectMLFormat}
                  btnColor='#1C5B9C'
                  sx={{ width: '10rem', height: '2.5rem', mt: 0 }} // 160px, 40px
                  onClick={updateProjectMLFormat}
                >
                  <Text>{`${t('save_materials_list_format_project_management')}`}</Text>
                </MobileButton>
              </Row>
            </Box>
          )}

          <Divider sx={{ mt: '1.125rem' }} />

          {/* Sites section */}
          <Row
            justifyContent={'center'}
            alignItems={'center'}
            sx={{ mb: '1rem' }}
          >
            <MobileButton
              btnColor='primary.main'
              sx={{ width: '100%', height: '2.5rem' }}
              onClick={handleAddSite}
            >
              <Icon
                height={'1.5rem'}
                width={'1.5rem'}
                sx={{ mr: '0.5rem' }}
                src={newProjectIcon}
              />
              <Text color='white'>{t('add_site_site_page')}</Text>
            </MobileButton>
          </Row>

          {/* Search section */}
          <Box sx={{ mb: 2 }}>
            <MobileInput
              label=''
              name='search'
              error={false}
              helperText=''
              onChange={e => handleSearchChange(e.target.value)}
              value={searchText}
              search
              compact
              placeholder={t('search_sites_placeholder')}
            />
          </Box>

          {/* Sites list */}
          <Box>
            {filteredSites.length > 0 ? (
              <Box sx={{ mb: 3 }}>
                {filteredSites.map(site => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    handleClick={() => handleSelectedSite(site)}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Text sx={{ color: '#6C757D' }}>{t('no_sites_found')}</Text>
              </Box>
            )}
          </Box>

          {/* Export buttons */}
          {filteredSites.length > 0 && (
            <Row sx={{ mb: 2 }}>
              <MobileButton
                btnColor='info.main'
                onClick={handleExportWord}
                loading={isReportGenerating}
                sx={{ mb: 0 }}
              >
                <Icon src={exportIcon} sx={{ mx: '0.25rem' }} />
                <Text sx={{ color: '#1C5B9C' }}>
                  {t('export_word_site_page')}
                </Text>
              </MobileButton>

              <Box width='1.25rem' />

              <MobileButton
                btnColor='info.main'
                onClick={handleExportExcel}
                sx={{ mb: 0 }}
              >
                <Icon src={excelIcon} sx={{ mx: '0.25rem' }} />
                <Text sx={{ color: '#1C5B9C' }}>
                  {t('export_excel_export_page')}
                </Text>
              </MobileButton>
            </Row>
          )}
        </Box>

        <Background />
      </Box>
      <ConfirmModal
        cancel={() => setShowModalConfirmDeleteProject(false)}
        confirm={() => {
          checkIfProjectHasSites();
        }}
        title={t('delete_project_confirmation_title')}
        show={showModalConfirmDeleteProject}
      />
      <ConfirmModal
        cancel={() => setShowModalConfirmDeleteAllSites(false)}
        confirm={() => {
          tryDeleteProject();
        }}
        title={t(
          'are_you_sure_you_want_to_delete_all_the_websites_in_this_project_confirmation_title'
        )}
        show={showModalConfirmDeleteAllSites}
      />
    </Layout>
  );
};
